import React, { useState, useEffect, useRef } from "react";
import { Button, CircularProgress, Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { securityClient } from 'securityClient';
import {
  useStartConnectMutation,
  useGetStatusQuery,
  useGetSyncRunStatusQuery,
} from "../../Services/hubspot";
import { useInitializeHubspotMutation, useLazyGetCompanyIdQuery } from "Services/company";
import { OnboardingLayout } from "./onBoardingLayOut";
import { useAppDispatch } from "State/hooks";
import { setCompany } from "State/companySlice";


type Phase = "idle" | "oauth_pending" | "sync_pending" | "error";

const ConnectHubspot: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [phase, setPhase] = useState<Phase>("idle");
  const [syncRunId, setSyncRunId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const oauthPopup = useRef<Window | null>(null);

  const [startConnect, { isLoading: isStarting }] = useStartConnectMutation();
  const [initializeHubspot] = useInitializeHubspotMutation();
  const [fetchCompanyId] = useLazyGetCompanyIdQuery();

  const hasToken = !!localStorage.getItem("idToken");

  useEffect(() => {
    const localToken = localStorage.getItem("idToken");
    if (localToken) {
      securityClient.setAccessTokenSilently(async () => localToken);
    }
  }, []);

  // Detect if the user manually closes the popup before completing OAuth
  useEffect(() => {
    if (phase !== "oauth_pending") return;

    const interval = setInterval(() => {
      if (oauthPopup.current?.closed) {
        oauthPopup.current = null;
        setPhase("idle");
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [phase]);

  // Listen for the auth code sent back from the popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'HUBSPOT_OAUTH_CODE') return;

      const authCode: string = event.data.code;

      // Resolve companyId from localStorage or API
      let companyId = localStorage.getItem("companyId");
      if (!companyId) {
        const { data } = await fetchCompanyId();
        companyId = data?.companyId ?? null;
      }

      if (!companyId) {
        setErrorMsg("Could not determine your company. Please try again.");
        setPhase("error");
        oauthPopup.current?.close();
        oauthPopup.current = null;
        return;
      }

      try {
        await initializeHubspot({ companyId, authCode });
        oauthPopup.current?.close();
        oauthPopup.current = null;
        // Status polling will detect connected: true and advance phase
      } catch (err: any) {
        setErrorMsg(err?.data?.message || "Failed to connect HubSpot. Please try again.");
        setPhase("error");
        oauthPopup.current?.close();
        oauthPopup.current = null;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchCompanyId, initializeHubspot]);

  // Phase 1: Poll OAuth status until connected
  const { data: statusData } = useGetStatusQuery(undefined, {
    skip: phase !== "oauth_pending" || !hasToken,
    pollingInterval: 3000,
  });

  useEffect(() => {
    if (phase !== "oauth_pending" || !statusData?.connected) return;
    if (!statusData.sync_run_id) return;

    oauthPopup.current?.close();
    oauthPopup.current = null;

    const companyId = statusData.tenant_id || localStorage.getItem("companyId");
    if (companyId) {
      setTenantId(companyId);
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("hubspotConnected", "true");
      dispatch(setCompany(companyId));
    }

    setSyncRunId(statusData.sync_run_id);
    setPhase("sync_pending");
  }, [statusData, phase, dispatch]);

  // Phase 2: Poll sync run status in the background — redirect if sync completes while user is still on the page
  const { data: syncStatusData } = useGetSyncRunStatusQuery(
    { companyId: tenantId!, syncRunId: syncRunId! },
    {
      skip: phase !== "sync_pending" || !syncRunId || !tenantId,
      pollingInterval: 3000,
    }
  );

  useEffect(() => {
    if (phase !== "sync_pending" || !syncStatusData) return;

    const { status } = syncStatusData;
    if (status === "complete" || status === "complete_with_errors") {
      navigate("/home");
    } else if (status === "failed") {
      setPhase("error");
      setErrorMsg("HubSpot sync failed. Please try again.");
    }
  }, [syncStatusData, phase, navigate]);

  const handleConnect = async () => {
    try {
      setErrorMsg(null);

      const token = await securityClient.getAccessTokenSilently?.();
      if (!token) throw new Error("User not authenticated");

      const result = await startConnect().unwrap();
      setPhase("oauth_pending");
      oauthPopup.current = window.open(result.authorization_url, 'hubspot_oauth', 'width=600,height=700');
    } catch (err: any) {
      console.error(err);
      const message = err?.data?.message || err?.message || "Unknown error";
      setErrorMsg(`Failed to start HubSpot connection: ${message}`);
      setPhase("error");
    }
  };

  const isProcessing = isStarting || phase === "oauth_pending";

  return (
    <OnboardingLayout currentStep={2}>
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Connect Your HubSpot Account
        </Typography>

        {phase === "idle" && (
          <Button variant="contained" color="primary" onClick={handleConnect}>
            Connect to HubSpot
          </Button>
        )}

        {isProcessing && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>
              {isStarting ? "Starting HubSpot connection..." : "Waiting for HubSpot authorization..."}
            </Typography>
          </Box>
        )}

        {phase === "sync_pending" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Your HubSpot sync is in progress
            </Typography>
            <Typography color="text.secondary">
              HubSpot syncs can take several minutes depending on the size of your account.
              You'll receive an email when your data is ready to explore.
            </Typography>
          </Box>
        )}

        {phase === "error" && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => {
              setPhase("idle");
              setErrorMsg(null);
            }}
          >
            Try Again
          </Button>
        )}

        {errorMsg && (
          <Typography sx={{ mt: 2, color: "red" }}>{errorMsg}</Typography>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default ConnectHubspot;
